/**
 * Sanitization HTML pour le rendu des mails dans le ThreadView.
 *
 * Port du pattern desktop (cf. mailfox/src/lib/components/threads/HtmlMessage.svelte).
 * On utilise DOMParser natif (pas de bibli type DOMPurify) — suffisant pour
 * notre besoin et plus léger.
 *
 * Stratégie :
 *   1. Strip tous les tags dangereux (script, style, iframe, etc.)
 *   2. Strip tous les attrs `on*` (event handlers)
 *   3. Neutralise href="javascript:" et autres protocols dangereux
 *   4. Bloque les images http:// par défaut → data-real-src + placeholder
 *      (le composant gère le toggle "Show images" en remplaçant src en place)
 *   5. Force tous les <a> à `target=_blank rel=noopener noreferrer`
 *   6. Wrap les `.gmail_quote` (citations) dans <details> collapsé
 */

const STRIPPED_TAGS =
  "script, link, style, object, embed, iframe, frame, frameset, base, meta, title, head";

/** Sanitize l'HTML brut d'un mail. Renvoie HTML sûr à injecter via {@html}. */
export function sanitizeEmailHtml(raw: string, allowRemoteImages = false): string {
  if (!raw) return "";

  const parsed = new DOMParser().parseFromString(raw, "text/html");

  // 1. Strip dangerous elements wholesale
  parsed.querySelectorAll(STRIPPED_TAGS).forEach((n) => n.remove());

  // 2. Strip event handlers + dangerous protocols
  parsed.querySelectorAll<HTMLElement>("*").forEach((el) => {
    [...el.attributes].forEach((a) => {
      const name = a.name.toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(a.name);
        return;
      }
      if (
        (name === "href" || name === "src" || name === "action") &&
        /^\s*(javascript|vbscript|data:text\/html):/i.test(a.value)
      ) {
        el.removeAttribute(a.name);
      }
    });
  });

  // 3. Block remote images by default — store URL in data-real-src
  if (!allowRemoteImages) {
    parsed
      .querySelectorAll<HTMLImageElement>('img[src^="http://"], img[src^="https://"]')
      .forEach((img) => {
        img.setAttribute("data-real-src", img.getAttribute("src") || "");
        img.setAttribute(
          "src",
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E",
        );
      });
  }

  // 4. cid: images : pas de support inline en Phase 4b — on neutralise
  //    avec une icône texte simple. Phase 4c pourra fetch les attachments.
  parsed.querySelectorAll<HTMLImageElement>('img[src^="cid:"]').forEach((img) => {
    img.setAttribute(
      "src",
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E",
    );
    if (!img.alt) img.setAttribute("alt", "[image inline]");
  });

  // 5. Force safe link behavior
  parsed.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });

  // 6. Fold Gmail citations (gmail_quote) dans <details>
  parsed.querySelectorAll(".gmail_quote, blockquote.gmail_quote").forEach((q) => {
    if (q.parentElement?.tagName === "DETAILS") return; // déjà wrapped
    const det = parsed.createElement("details");
    det.className = "mf-quote";
    const sum = parsed.createElement("summary");
    sum.textContent = "Citations précédentes";
    det.appendChild(sum);
    q.parentNode?.insertBefore(det, q);
    det.appendChild(q);
  });

  return parsed.body.innerHTML;
}

/** True si l'HTML contient au moins une image distante. */
export function hasRemoteImages(html: string): boolean {
  return /<img[^>]+src=["'](https?:|\/\/)/i.test(html);
}
